import { StandardEvents } from '@shopify/events';

/**
 * Quantity steppers and a live order summary for the dummy grid.
 *
 * The adding itself is Horizon's: every card wraps a real
 * `product-form-component`, so the cart drawer, the header count and the error
 * states are the ones used everywhere else. This element only does the two
 * things Horizon has no opinion about — driving the +/- buttons, and keeping
 * the summary panel in step with the cart after each add.
 */
class DummyOrderGrid extends HTMLElement {
  // Six in the basket is what the discount needs: buy five, the sixth is free.
  #promoEvery = 6;
  #signature = null;
  #before = null;
  #timer = null;
  #attempts = 0;

  #onCartUpdate = () => this.#scheduleRefresh();

  connectedCallback() {
    this.addEventListener('click', this.#onStep);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, true);
    // Establish the baseline the retry loop compares against. Without it the
    // first add is measured against `null`, an empty cart reads as "changed",
    // and the loop stops one step behind reality.
    this.#refresh();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onStep);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, true);
    clearTimeout(this.#timer);
  }

  /**
   * Horizon fires `cartLinesUpdate` before the cart session has settled: read
   * `/cart.js` in that instant and it still reports the previous contents, so a
   * single refresh painted the panel with stale numbers and left the running
   * total at $0.00 no matter how many colours were added.
   *
   * Rather than guess one delay that is long enough, read until the answer
   * stops matching what is already on screen, then stop. Four tries over about
   * a second covers a slow response without spinning if nothing really changed.
   */
  #scheduleRefresh() {
    clearTimeout(this.#timer);
    this.#attempts = 0;
    this.#before = this.#signature;
    this.#tick();
  }

  #tick = async () => {
    await this.#refresh();
    this.#attempts += 1;
    if (this.#signature === this.#before && this.#attempts < 5) {
      this.#timer = setTimeout(this.#tick, 200 * this.#attempts);
    }
  };

  #onStep = (event) => {
    const button = event.target.closest('[data-step]');
    if (!button) return;

    const field = button.parentElement?.querySelector('.dummy-grid__quantity');
    if (!(field instanceof HTMLInputElement)) return;

    const min = Number(field.min) || 1;
    const max = Number(field.max) || Infinity;
    const next = (Number(field.value) || min) + Number(button.dataset.step);
    field.value = String(Math.min(max, Math.max(min, next)));
  };

  async #refresh() {
    // The cart event carries only what changed, and the panel shows totals for
    // the whole product, so read the cart back rather than trying to patch it.
    let cart;
    try {
      // `no-store`, or the browser keeps serving the first answer it got — the
      // empty cart read on page load — and every retry re-reads the same stale
      // body while the real cart fills up behind it.
      const response = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.js`, {
        cache: 'no-store',
      });
      if (!response.ok) return;
      cart = await response.json();
    } catch {
      return;
    }

    const productId = Number(this.dataset.productId);
    const mine = cart.items.filter((item) => item.product_id === productId);
    const subtotal = mine.reduce((total, item) => total + item.final_line_price, 0);

    this.#signature = mine.map((item) => `${item.variant_id}:${item.quantity}`).join(',');

    const list = this.querySelector('[ref="lines"]');
    if (list) {
      list.innerHTML = mine.map((item) => this.#line(item)).join('');
    }

    const empty = this.querySelector('[ref="empty"]');
    empty?.classList.toggle('hidden', mine.length > 0);

    const total = this.querySelector('[ref="subtotal"]');
    if (total) total.textContent = this.#money(subtotal);

    // The phone bar. It only exists once there is something to total, so an
    // untouched page is not carrying a strip that says $0.00.
    this.classList.toggle('dummy-grid--has-items', mine.length > 0);
    const barCount = this.querySelector('[ref="barCount"]');
    if (barCount) barCount.textContent = String(mine.length);
    const barSubtotal = this.querySelector('[ref="barSubtotal"]');
    if (barSubtotal) barSubtotal.textContent = this.#money(subtotal);

    this.#promo(cart);
  }

  // "Buy 5, get 1 free" is an automatic Shopify discount that counts dummies
  // across BOTH products, so this counts the whole cart by product type rather
  // than this page's lines. The offer only shows at six, which is the part
  // customers get wrong: someone who adds five sees nothing and assumes it is
  // broken. Saying how many more are needed is what turns five into six.
  #promo(cart) {
    const node = this.querySelector('[ref="promo"]');
    const text = this.querySelector('[ref="promoText"]');
    if (!node || !text) return;

    const idle = node.dataset.promoIdle ?? '';
    const dummies = cart.items
      .filter((item) => item.product_type === 'Magnetic Dummy')
      .reduce((total, item) => total + item.quantity, 0);

    if (dummies === 0) {
      text.textContent = idle;
      return;
    }
    const free = Math.floor(dummies / this.#promoEvery);
    const missing = (this.#promoEvery - (dummies % this.#promoEvery)) % this.#promoEvery;
    if (missing === 0) {
      text.textContent = free === 1 ? 'One of these is free 🎁' : `${free} of these are free 🎁`;
    } else if (free > 0) {
      text.textContent = `${free} free so far — add ${missing} more for another 🎁`;
    } else {
      text.textContent = missing === 1
        ? 'Add 1 more and one is free 🎁'
        : `Add ${missing} more and one is free 🎁`;
    }
  }

  #line(item) {
    // The URL comes from Shopify's own cart, so this is belt-and-braces rather
    // than a hole being closed — but an unescaped quote in an attribute is the
    // kind of thing that only breaks once someone renames a file oddly.
    const image = item.image
      ? `<img class="dummy-grid__thumb" src="${escapeHtml(item.image)}" alt="" loading="lazy" width="60" height="60">`
      : '<span class="dummy-grid__thumb"></span>';
    return `<li class="dummy-grid__line">
      ${image}
      <span class="dummy-grid__line-name">${escapeHtml(item.variant_title ?? item.title)}
        <small>Qty: ${item.quantity}</small>
      </span>
      <span class="dummy-grid__line-price">${this.#money(item.final_line_price)}</span>
    </li>`;
  }

  /** Formats cents with the shop's own money format, so currency and separators
   *  match what Liquid rendered on first paint. */
  #money(cents) {
    const amount = (cents / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const format = this.dataset.moneyFormat || '${{amount}}';
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }
}

function escapeHtml(value) {
  return String(value).replace(
    /[&<>"']/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]
  );
}

if (!customElements.get('dummy-order-grid')) {
  customElements.define('dummy-order-grid', DummyOrderGrid);
}
